import { capitalize, Stack, Typography } from '@mui/material';
import { type LogRecord } from '@repo/api-gw-sdk';

import { MultiSelectFilter } from '@/components/queryBuilder/filters';
import { PropertyType, type FilterProperty } from '@/types/advanceFilter';

import { LogTimeCell, severities, SeverityCircle } from './logTimeCell';

const timestamp: FilterProperty<LogRecord> = {
  name: 'timestamp',
  displayName: 'Timestamp',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) =>
      row.original.timestamp && <LogTimeCell entry={row.original} />,
  },
};

const raw: FilterProperty<LogRecord> = {
  name: 'raw',
  displayName: 'Raw',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) =>
      row.original.raw && <Typography>{row.original.raw}</Typography>,
  },
};

export const generateColumns = (logs: LogRecord[]) =>
  logs
    .reduce<string[]>((acc, log) => {
      Object.keys(log.fields || {}).forEach((key) => {
        if (!acc.includes(key)) {
          acc.push(key);
        }
      });
      return acc;
    }, [])
    .sort()
    .filter((x) => ![level.name.replace('.', '')].includes(x))
    .map(
      (name) =>
        ({
          name,
          displayName: name,
          type: PropertyType.String,
          group: '',
          column: {
            cell: ({ row }) =>
              row.original.fields &&
              name in row.original.fields && (
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                <Typography>{(row.original.fields as any)[name]}</Typography>
              ),
          },
          field: {
            Component: ({ entity }) => {
              return (
                entity.fields &&
                name in entity.fields && (
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  <Typography>{(entity.fields as any)[name]}</Typography>
                )
              );
            },
          },
        }) satisfies FilterProperty<LogRecord>
    );

const level: FilterProperty<LogRecord> = {
  name: '.level',
  displayName: 'Level',
  type: PropertyType.String,
  group: '',
  accessorFn: (row) => row.level,
  column: {
    enableHiding: false,
  },
  field: {
    Component: ({ entity }) => {
      return (
        entity.level && (
          <Stack direction='row' alignItems='center' gap='12px'>
            <SeverityCircle severity={entity.level} />
            <Typography>
              {capitalize(entity.level.toLocaleLowerCase())}
            </Typography>
          </Stack>
        )
      );
    },
  },
  filter: {
    expandedByDefault: true,
    Component: (props) => (
      <MultiSelectFilter
        {...props}
        options={Object.keys(severities).map((id) => ({
          id,
          name: capitalize(id.toLocaleLowerCase()),
          Image: <SeverityCircle severity={id} />,
        }))}
      />
    ),
  },
};

export const logSpaceSummaryFields: FilterProperty<LogRecord>[] = [level];
export const defaultColumns: FilterProperty<LogRecord>[] = [
  level,
  timestamp,
  raw,
];
